import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTooltip } from '../components/TooltipSystem';
import { useBranding } from '../../context/BrandingContext';
import {
  Trophy, Plus, Swords, Users, Clock, Star, ArrowLeft, Crown,
  Gamepad2, ChevronDown, Trash2, Play, CheckCircle2, AlertTriangle,
  Target, Medal, Zap, CalendarDays, BarChart3, Edit2, Eye
} from 'lucide-react';

const GAME_TYPES = [
  { id: 'wordle', label: 'Wordle', icon: '🔤', desc: 'Indovina la parola' },
  { id: 'trivia', label: 'Trivia', icon: '🧠', desc: 'Quiz a risposta multipla' },
  { id: 'tango', label: 'Tango', icon: '🧩', desc: 'Puzzle griglia' },
];

const DIFFICULTIES = [
  { id: 'easy', label: 'Facile', color: '#10B981', bg: 'bg-emerald-50', mult: '1x' },
  { id: 'medium', label: 'Medio', color: '#F59E0B', bg: 'bg-amber-50', mult: '1.5x' },
  { id: 'hard', label: 'Difficile', color: '#EF4444', bg: 'bg-red-50', mult: '2x' },
];

const TARGET_TYPES = [
  { id: 'all', label: 'Tutti i clienti' },
  { id: 'tier', label: 'Per livello (tier)' },
  { id: 'min_visits', label: 'Min. visite' },
  { id: 'invite_list', label: 'Lista invitati' },
];

const STATUS_STYLES = {
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', label: 'Bozza' },
  active: { bg: 'bg-orange-50', text: 'text-[#D44E20]', border: 'border-orange-200/50', label: 'Attiva' },
  completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200/50', label: 'Completata' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return <span className={`${s.bg} ${s.text} ${s.border} border rounded-full px-3 py-1 text-[11px] font-semibold`} data-testid={`status-${status}`}>{s.label}</span>;
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon size={18} style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
          <p className="text-xs text-[#64748B] font-medium">{label}</p>
        </div>
      </div>
    </motion.div>
  );
}

/* ── Match Detail View ── */
function MatchDetail({ match, onBack, onRefresh }) {
  const { tier_names } = useBranding();
  const tierLabel = (t) => tier_names?.[t] || (t ? t[0].toUpperCase() + t.slice(1) : '—');
  const [completing, setCompleting] = useState(false);
  const [activating, setActivating] = useState(false);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await adminApi.post(`/admin/special-matches/${match.id}/activate`);
      toast.success('Partita attivata!');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setActivating(false); }
  };

  const handleComplete = async () => {
    if (!window.confirm('Vuoi completare questa partita e assegnare i premi?')) return;
    setCompleting(true);
    try {
      const r = await adminApi.post(`/admin/special-matches/${match.id}/complete`);
      toast.success(r.data.message);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setCompleting(false); }
  };

  const gt = GAME_TYPES.find(g => g.id === match.game_type);
  const diff = DIFFICULTIES.find(d => d.id === match.difficulty);
  const lb = match.leaderboard || [];

  return (
    <div className="space-y-6">
      <button onClick={onBack} data-testid="match-detail-back"
        className="flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#1A1A1A] transition-colors">
        <ArrowLeft size={16} /> Torna alla lista
      </button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-[#1A1A1A]">{match.name}</h2>
              <StatusBadge status={match.status} />
            </div>
            {match.description && <p className="text-[#64748B] text-sm mb-4">{match.description}</p>}
            <div className="flex flex-wrap gap-3">
              <span className="flex items-center gap-1.5 text-xs font-medium text-[#64748B] bg-slate-50 rounded-lg px-3 py-1.5">
                {gt?.icon} {gt?.label || match.game_type}
              </span>
              <span className={`flex items-center gap-1.5 text-xs font-medium rounded-lg px-3 py-1.5 ${diff?.bg || 'bg-slate-50'}`} style={{ color: diff?.color }}>
                <Target size={12} /> {diff?.label || match.difficulty} ({diff?.mult || '1x'})
              </span>
              <span className="flex items-center gap-1.5 text-xs font-medium text-[#64748B] bg-slate-50 rounded-lg px-3 py-1.5">
                <Users size={12} /> {match.participants_count || 0} partecipanti
              </span>
              <span className="flex items-center gap-1.5 text-xs font-medium text-[#64748B] bg-slate-50 rounded-lg px-3 py-1.5">
                <Trophy size={12} /> Top {match.prize_top_n}: {match.prize_description || '—'}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            {match.status === 'draft' && (
              <button onClick={handleActivate} disabled={activating} data-testid="activate-match-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#D44E20] hover:bg-[#B3421A] disabled:opacity-50 transition-colors">
                <Play size={14} /> {activating ? 'Attivazione...' : 'Attiva'}
              </button>
            )}
            {match.status === 'active' && (
              <button onClick={handleComplete} disabled={completing} data-testid="complete-match-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 transition-colors">
                <CheckCircle2 size={14} /> {completing ? 'Completamento...' : 'Completa e premia'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2 mb-4">
          <Crown size={18} className="text-[#F59E0B]" /> Classifica
        </h3>
        {lb.length === 0 ? (
          <div className="text-center py-8">
            <Users size={32} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-[#64748B]">Nessun partecipante ancora</p>
          </div>
        ) : (
          <table className="w-full" data-testid="leaderboard-table">
            <thead>
              <tr>
                <th className="text-left text-[10px] uppercase tracking-wider text-[#64748B] pb-3 border-b border-slate-200/60 w-12">#</th>
                <th className="text-left text-[10px] uppercase tracking-wider text-[#64748B] pb-3 border-b border-slate-200/60">Giocatore</th>
                <th className="text-left text-[10px] uppercase tracking-wider text-[#64748B] pb-3 border-b border-slate-200/60">Tier</th>
                <th className="text-right text-[10px] uppercase tracking-wider text-[#64748B] pb-3 border-b border-slate-200/60">Punteggio</th>
                <th className="text-right text-[10px] uppercase tracking-wider text-[#64748B] pb-3 border-b border-slate-200/60">Tentativi</th>
              </tr>
            </thead>
            <tbody>
              {lb.map((entry, idx) => (
                <motion.tr key={idx} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.05 }}
                  className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${idx < (match.prize_top_n || 1) ? 'bg-amber-50/30' : ''}`}>
                  <td className="py-3 text-sm font-bold text-[#1A1A1A]">
                    {idx === 0 ? <Medal size={16} className="text-yellow-500" /> :
                     idx === 1 ? <Medal size={16} className="text-slate-400" /> :
                     idx === 2 ? <Medal size={16} className="text-amber-700" /> :
                     <span className="text-[#64748B]">{idx + 1}</span>}
                  </td>
                  <td className="py-3 text-sm font-medium text-[#1A1A1A]">{entry.name}</td>
                  <td className="py-3"><span className="text-xs font-medium text-[#64748B] bg-slate-100 rounded-full px-2 py-0.5 capitalize">{tierLabel(entry.tier)}</span></td>
                  <td className="py-3 text-right text-sm font-bold text-[#D44E20]">{entry.best_score}</td>
                  <td className="py-3 text-right text-sm text-[#64748B]">{entry.attempts}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── Create/Edit Form ── */
function MatchForm({ match, onSave, onCancel }) {
  const [form, setForm] = useState(match || {
    name: '', description: '', game_type: 'wordle', difficulty: 'medium',
    target_type: 'all', target_value: [], prize_type: 'points', prize_value: 50,
    prize_description: '', prize_top_n: 1, max_attempts: 3,
    start_date: '', end_date: '', max_participants: null, leaderboard_enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [tierInput, setTierInput] = useState((match?.target_value || []).join(', '));

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Nome partita obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.target_type === 'tier') payload.target_value = tierInput.split(',').map(t => t.trim()).filter(Boolean);
      if (form.target_type === 'min_visits') payload.target_value = [String(form.target_value_min || 0)];
      await onSave(payload);
    } finally { setSaving(false); }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <button onClick={onCancel} className="flex items-center gap-1.5 text-sm font-medium text-[#64748B] hover:text-[#1A1A1A] transition-colors">
        <ArrowLeft size={16} /> Annulla
      </button>

      <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm space-y-5">
        <h3 className="text-lg font-semibold text-[#1A1A1A]">{match ? 'Modifica partita' : 'Nuova partita speciale'}</h3>

        {/* Name + Description */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Nome partita</label>
            <input value={form.name} onChange={e => upd('name', e.target.value)} data-testid="match-name-input"
              placeholder="es: Torneo Wordle Estivo"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none transition-colors" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Descrizione</label>
            <input value={form.description} onChange={e => upd('description', e.target.value)} data-testid="match-desc-input"
              placeholder="Sfida i migliori giocatori!"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none transition-colors" />
          </div>
        </div>

        {/* Game Type */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Tipo di gioco</label>
          <div className="grid grid-cols-3 gap-3">
            {GAME_TYPES.map(g => (
              <button key={g.id} onClick={() => upd('game_type', g.id)} data-testid={`game-type-${g.id}`}
                className={`p-3 rounded-xl border-2 text-left transition-all ${form.game_type === g.id ? 'border-[#D44E20] bg-[#FFF5F2]' : 'border-slate-200 hover:border-slate-300'}`}>
                <span className="text-xl">{g.icon}</span>
                <p className="text-sm font-semibold text-[#1A1A1A] mt-1">{g.label}</p>
                <p className="text-[10px] text-[#64748B]">{g.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Difficulty */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Difficoltà</label>
          <div className="flex gap-3">
            {DIFFICULTIES.map(d => (
              <button key={d.id} onClick={() => upd('difficulty', d.id)} data-testid={`difficulty-${d.id}`}
                className={`flex-1 p-3 rounded-xl border-2 text-center transition-all ${form.difficulty === d.id ? `border-current ${d.bg}` : 'border-slate-200 hover:border-slate-300'}`}
                style={form.difficulty === d.id ? { borderColor: d.color, color: d.color } : {}}>
                <p className="text-sm font-bold">{d.label}</p>
                <p className="text-[10px] text-[#64748B]">Moltiplicatore {d.mult}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Target audience */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-2">Target</label>
          <select value={form.target_type} onChange={e => upd('target_type', e.target.value)} data-testid="target-type-select"
            className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
            {TARGET_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          {form.target_type === 'tier' && (
            <input value={tierInput} onChange={e => setTierInput(e.target.value)} placeholder="bronze, silver, gold, platinum"
              className="w-full h-10 mt-2 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          )}
          {form.target_type === 'min_visits' && (
            <input type="number" value={form.target_value_min || ''} onChange={e => upd('target_value_min', parseInt(e.target.value) || 0)} placeholder="Min. visite richieste"
              className="w-full h-10 mt-2 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          )}
        </div>

        {/* Prize */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Tipo premio</label>
            <select value={form.prize_type} onChange={e => upd('prize_type', e.target.value)} data-testid="prize-type-select"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none">
              <option value="points">Punti</option>
              <option value="reward">Premio (da lista)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">{form.prize_type === 'points' ? 'Punti' : 'ID premio'}</label>
            <input type={form.prize_type === 'points' ? 'number' : 'text'} value={form.prize_value} onChange={e => upd('prize_value', form.prize_type === 'points' ? parseInt(e.target.value) || 0 : e.target.value)}
              data-testid="prize-value-input"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Top N premiati</label>
            <input type="number" value={form.prize_top_n} onChange={e => upd('prize_top_n', parseInt(e.target.value) || 1)} data-testid="prize-top-n-input"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Descrizione premio (visibile ai clienti)</label>
          <input value={form.prize_description} onChange={e => upd('prize_description', e.target.value)} data-testid="prize-desc-input"
            placeholder="es: 100 punti bonus al vincitore" className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
        </div>

        {/* Dates & Limits */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Data inizio</label>
            <input type="datetime-local" value={form.start_date?.slice(0, 16) || ''} onChange={e => upd('start_date', e.target.value ? new Date(e.target.value).toISOString() : '')}
              data-testid="start-date-input"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Data fine</label>
            <input type="datetime-local" value={form.end_date?.slice(0, 16) || ''} onChange={e => upd('end_date', e.target.value ? new Date(e.target.value).toISOString() : '')}
              data-testid="end-date-input"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Max partecipanti</label>
            <input type="number" value={form.max_participants || ''} onChange={e => upd('max_participants', parseInt(e.target.value) || null)} placeholder="Illimitati"
              data-testid="max-participants-input"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">Max tentativi</label>
            <input type="number" value={form.max_attempts} onChange={e => upd('max_attempts', parseInt(e.target.value) || 1)}
              data-testid="max-attempts-input"
              className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm text-[#1A1A1A] focus:border-[#D44E20] focus:outline-none" />
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onCancel} className="px-5 py-2.5 rounded-xl text-sm font-medium text-[#64748B] border border-slate-200 hover:bg-slate-50 transition-colors">
            Annulla
          </button>
          <button onClick={handleSubmit} disabled={saving} data-testid="save-match-btn"
            className="px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] disabled:opacity-50 transition-colors">
            {saving ? 'Salvataggio...' : match ? 'Salva modifiche' : 'Crea partita'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function AdminSpecialMatches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | detail | create | edit
  const [selected, setSelected] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    try {
      const r = await adminApi.get('/admin/special-matches');
      setMatches(r.data);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const r = await adminApi.get(`/admin/special-matches/${id}`);
      setSelected(r.data);
      setView('detail');
    } catch { toast.error('Errore caricamento dettaglio'); }
  };

  const handleCreate = async (data) => {
    try {
      await adminApi.post('/admin/special-matches', data);
      toast.success('Partita creata!');
      setView('list');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore creazione'); }
  };

  const handleUpdate = async (data) => {
    try {
      await adminApi.put(`/admin/special-matches/${selected.id}`, data);
      toast.success('Partita aggiornata!');
      setView('list');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore aggiornamento'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa partita?')) return;
    setDeleting(id);
    try {
      await adminApi.delete(`/admin/special-matches/${id}`);
      toast.success('Eliminata');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setDeleting(null); }
  };

  const activeCount = matches.filter(m => m.status === 'active').length;
  const totalParticipants = matches.reduce((s, m) => s + (m.participants_count || 0), 0);
  const completedCount = matches.filter(m => m.status === 'completed').length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  if (view === 'create') return <div className="p-6 max-w-4xl mx-auto"><MatchForm onSave={handleCreate} onCancel={() => setView('list')} /></div>;
  if (view === 'edit' && selected) return <div className="p-6 max-w-4xl mx-auto"><MatchForm match={selected} onSave={handleUpdate} onCancel={() => setView('list')} /></div>;
  if (view === 'detail' && selected) return (
    <div className="p-6 max-w-4xl mx-auto">
      <MatchDetail match={selected} onBack={() => { setView('list'); setSelected(null); }} onRefresh={() => openDetail(selected.id)} />
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><Swords size={22} className="text-[#D44E20]" /> Partite Speciali</h1>
          <p className="text-sm text-[#64748B] mt-1">Crea tornei e sfide competitive con classifica e premi</p>
        </div>
        <button onClick={() => setView('create')} data-testid="create-match-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] transition-colors active:scale-95">
          <Plus size={16} /> Nuova partita
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Zap} label="Partite attive" value={activeCount} color="#D44E20" />
        <StatCard icon={Users} label="Partecipanti totali" value={totalParticipants} color="#3B82F6" />
        <StatCard icon={Trophy} label="Partite completate" value={completedCount} color="#10B981" />
      </div>

      {/* Match List */}
      {matches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center shadow-sm">
          <Swords size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-[#1A1A1A] font-semibold">Nessuna partita speciale</p>
          <p className="text-sm text-[#64748B] mt-1">Crea la tua prima partita competitiva</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {matches.map((m, idx) => {
              const gt = GAME_TYPES.find(g => g.id === m.game_type);
              const diff = DIFFICULTIES.find(d => d.id === m.difficulty);
              const endStr = m.end_date ? new Date(m.end_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
              return (
                <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: idx * 0.04 }}
                  data-testid={`match-card-${m.id}`}
                  className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                  onClick={() => openDetail(m.id)}>
                  <div className="flex items-center justify-between">
                    <PageTooltip page="special-matches" />
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl" style={{ backgroundColor: `${diff?.color || '#64748B'}12` }}>
                        {gt?.icon || '🎮'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-[#1A1A1A]">{m.name}</p>
                          <StatusBadge status={m.status} />
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[11px] text-[#64748B]">{gt?.label}</span>
                          <span className="text-[11px] font-medium" style={{ color: diff?.color }}>{diff?.label}</span>
                          <span className="text-[11px] text-[#64748B] flex items-center gap-1"><Users size={10} /> {m.participants_count || 0}</span>
                          <span className="text-[11px] text-[#64748B] flex items-center gap-1"><CalendarDays size={10} /> {endStr}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      {m.status === 'draft' && (
                        <>
                          <button onClick={() => { setSelected(m); setView('edit'); }} data-testid={`edit-match-${m.id}`}
                            className="p-2 rounded-lg text-[#64748B] hover:bg-slate-100 transition-colors"><Edit2 size={14} /></button>
                          <button onClick={() => handleDelete(m.id)} disabled={deleting === m.id} data-testid={`delete-match-${m.id}`}
                            className="p-2 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"><Trash2 size={14} /></button>
                        </>
                      )}
                      <button onClick={() => openDetail(m.id)} className="p-2 rounded-lg text-[#64748B] hover:bg-slate-100 transition-colors"><Eye size={14} /></button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
